/**
 * Ground-truth-gated barn_form verification (issue #147, gated by #157). The
 * barn_form classifier's accuracy on the hand-checked sample is only 6/12
 * (#157 review comment), so the open-front/open-sided void-face rendering in
 * buildings.ts must not trust every classified barn_form -- only buildings
 * whose committed `appearance.barn_form` (data/config/buildings.json) AGREES
 * with the independent hand-check in data/config/appearance_ground_truth.json.
 * Pure and three.js-free, like facadeGrid/doorSlot, so the derivation is
 * unit-tested directly rather than the rendering code hardcoding way-ids.
 * Widen (or remove) this gate only when #157 lands a materially more
 * accurate classifier -- not by editing this file's callers piecemeal.
 */

import type { AppearanceGroundTruth } from '../core/manifest';

export type BarnForm = 'enclosed' | 'open_front' | 'open_sided';

/** The subset of BarnForm that gets a rendering treatment (issue #147); `enclosed` is today's byte-identical box. */
export type OpenBarnForm = 'open_front' | 'open_sided';

export type { AppearanceGroundTruth };

function isBarnForm(value: unknown): value is BarnForm {
  return value === 'enclosed' || value === 'open_front' || value === 'open_sided';
}

/**
 * The ground-truth-verified open_front/open_sided barns: buildings whose own
 * committed `appearance.barn_form` agrees with their appearance_ground_truth.json
 * entry, restricted to the two forms the void-face treatment applies to
 * (an `enclosed` agreement has nothing to render differently, so it is
 * excluded from the result even though it "agrees").
 *
 * Falls out of the result (never guessed, hard constraint #7) for any of:
 * a malformed/absent committed barn_form, no ground-truth entry for that id,
 * or a disagreement between the two. An EMPTY map is a legitimate result --
 * it means classifier accuracy hasn't reached the #157 bar yet across this
 * corpus snapshot, not a bug in this function.
 */
export function verifiedOpenBarnForms(
  buildings: ReadonlyArray<{ id: string; appearance?: { barn_form?: unknown } }>,
  groundTruth: AppearanceGroundTruth,
): Map<string, OpenBarnForm> {
  const verified = new Map<string, OpenBarnForm>();
  for (const b of buildings) {
    const committed = b.appearance?.barn_form;
    if (!isBarnForm(committed) || committed === 'enclosed') continue;
    const truthEntry = groundTruth[b.id];
    if (truthEntry?.fields?.barn_form === committed) {
      verified.set(b.id, committed);
    }
  }
  return verified;
}
